/**
 * TreeParts.jsx - the pieces the Projects and Opportunities tables draw their
 * main project > sub-project > PO line tree with (10/14/26, Sage-style):
 *   TreeControls    "Group by main project" switch, Expand all / Collapse all
 *   TreeGroupLabel  a group row's name cell: chevron, number, name, count
 *   TreeIndent      a record's name cell under a group: indented, with a
 *                   connector to its group
 * The tree lives in the Description column (or the first column shown when
 * Description is hidden), the column that names the work - like Sage's
 * Project name. Module scope, never defined in a render body.
 *
 * How the group rows look is the view's (Customize > Group rows, 10/14/26 -
 * tableViews/treeLook.js): band and text colour, bold, size, the project #
 * and count badges, the connectors. `tree` is view.tree; without one the
 * rows look as they did when the tree shipped.
 */
import { Icon } from '../../ui/Icon';
import { Switch } from '../../tableViews/customize/controls';
import { levelLook } from '../../tableViews/treeLook';

const STEP = 16; // px per level
const LINE = '#A9BBA0';
const btn = 'text-[11px] font-semibold text-gat-greenDark hover:underline bg-transparent border-0 p-0 cursor-pointer whitespace-nowrap';

export function TreeControls({ tree }) {
  if (!tree || !tree.available) return null;
  return (
    <span className="inline-flex items-center gap-2 flex-wrap" data-testid="tree-controls">
      <Switch small on={tree.on} onChange={tree.setOn} label="Group by main project" />
      <span className="text-[11.5px] font-semibold text-text-body whitespace-nowrap">Group by main project</span>
      {tree.on && tree.groupCount > 0 && !tree.searching && (
        <>
          <span className="w-px h-4 bg-border-strong" />
          <button type="button" className={btn} onClick={tree.expandAll}>
            Expand all
          </button>
          <button type="button" className={btn} onClick={tree.collapseAll}>
            Collapse all
          </button>
        </>
      )}
    </span>
  );
}

function Connector() {
  return (
    <span
      aria-hidden
      style={{
        flex: '0 0 auto',
        width: 11,
        height: 9,
        marginRight: 5,
        marginTop: 1,
        borderLeft: `1.5px solid ${LINE}`,
        borderBottom: `1.5px solid ${LINE}`,
        borderBottomLeftRadius: 3,
      }}
    />
  );
}

/** A record's cell under a group: indented by its depth, with a connector (unless the view switched them off). Top-level rows draw as before. */
export function TreeIndent({ item, children, lines = true }) {
  if (!item || !item.depth) return children;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', paddingLeft: (item.depth - 1) * STEP + (lines ? 4 : 10) }}>
      {lines && <Connector />}
      <div style={{ minWidth: 0, flex: 1 }}>{children}</div>
    </div>
  );
}

const countText = (n) => (n.records.length < n.total ? `${n.records.length} of ${n.total}` : String(n.total));

/** A group row's name cell: the chevron (opens / closes it), its number, its name and how many records it holds. */
export function TreeGroupLabel({ item, onToggle, tree }) {
  const n = item.node;
  const main = n.level === 'main';
  const what = main ? 'main project' : 'sub-project';
  const look = levelLook(tree, n.level);
  const lines = !tree || tree.lines !== false;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        paddingLeft: item.depth ? (item.depth - 1) * STEP + (lines ? 4 : 10) : 0,
        minWidth: 0,
      }}
    >
      {item.depth > 0 && lines && <Connector />}
      <button
        type="button"
        aria-expanded={!!item.open}
        aria-label={`${item.open ? 'Collapse' : 'Expand'} ${what} ${n.gpn || n.label}`}
        title={item.open ? 'Collapse' : 'Expand'}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(n.key);
        }}
        style={{
          flex: '0 0 auto',
          width: 18,
          height: 18,
          borderRadius: 4,
          border: '1px solid #C5D3BC',
          background: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <span style={{ display: 'inline-flex', transform: item.open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms ease-out' }}>
          <Icon name="chevron" size={10} color="#1B5E20" />
        </span>
      </button>
      {/* inline, so a long name wraps by word under its number instead of one letter a line */}
      <div style={{ flex: '1 1 auto', minWidth: 0, lineHeight: '17px', overflowWrap: 'normal', wordBreak: 'normal' }}>
        {n.gpn && look.number && (
          <span
            className="font-mono"
            style={{
              display: 'inline-block',
              marginRight: 6,
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '15px',
              color: main ? '#fff' : '#1B5E20',
              background: main ? '#2E7D32' : '#E3EEDA',
              borderRadius: 4,
              padding: '0 5px',
              whiteSpace: 'nowrap',
            }}
          >
            {n.gpn}
          </span>
        )}
        <span style={{ fontWeight: look.bold ? (main ? 800 : 700) : 500, color: look.font || '#0D0D0D' }} title={n.label}>
          {n.label || (main ? 'Main project' : 'Sub-project')}
        </span>
        {look.count && (
          <span
            data-testid="group-count"
            title={`${n.total} record${n.total === 1 ? '' : 's'} in this ${what}${n.records.length < n.total ? ` - ${n.records.length} match the filters` : ''}`}
            style={{
              display: 'inline-block',
              marginLeft: 6,
              fontSize: 9.5,
              fontWeight: 800,
              color: '#1B5E20',
              background: '#fff',
              border: '1px solid #C5D3BC',
              borderRadius: 10,
              padding: '0 6px',
              lineHeight: '14px',
              whiteSpace: 'nowrap',
            }}
          >
            {countText(n)}
          </span>
        )}
      </div>
    </div>
  );
}

/** '#RRGGBB' a little darker: a group row's hover. */
const darker = (hex, f = 0.08) =>
  `#${[1, 3, 5]
    .map((i) =>
      Math.max(0, Math.round(parseInt(hex.slice(i, i + 2), 16) * (1 - f)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`.toUpperCase();

/**
 * A group row's look (both tables), from the view's tree look:
 *   rowProps   the <tr>'s attributes - its band colour (through --health-tint,
 *              which index.css paints on every cell) and weight
 *   cellStyle  every cell's text colour and size (a cell's own colour class loses to it)
 *   totals     whether the row shows the roll-up figures
 */
export function groupLook(item, tree) {
  const look = levelLook(tree, item.node.level);
  return {
    rowProps: {
      'data-health': 'group',
      'data-group': item.node.level,
      style: { '--health-tint': look.fill, '--health-hover': darker(look.fill), fontWeight: look.bold ? 600 : 400 },
    },
    cellStyle: { ...(look.font ? { color: look.font } : {}), ...(look.size ? { fontSize: look.size } : {}) },
    totals: look.totals,
  };
}

/** A record shown because it matches while its siblings are filtered out: a thin amber edge marks the hit. */
export const matchStyle = (item) => (item && item.partial ? { boxShadow: 'inset 3px 0 0 #F9A825' } : null);
